import React, { useState, useEffect, useRef, useCallback } from "react";
import { ChannelVideosTab } from "./ChannelVideosTab";
import NexusAnneTab from "./NexusAnneTab";
import { Mic, Send, Layers, PlayCircle, Loader2, Volume2, VolumeX, Play, Trash2, Sparkles, Square, Paperclip, X, Brain, Copy, Link2, ShieldCheck } from "lucide-react";
import { LiveClock } from "./LiveClock";
import { HerculesScorecard } from "./HerculesScorecard";
import { FloorLockStrip } from "./FloorLockStrip";
import { Linkified } from "./Linkified";
import { nexusChannel, nexusChatHistory, nexusChatClear, nexusCommand, nexusTalk, nexusCommandAttach, audioUrlForKey, nexusCancel, ponytailCraft, ponytailJob, ponytailLast, floorCommand, floorJob, floorHalt, floorResume, channelVideos } from "../lib/api";
import { toast } from "sonner";
import { MissionsStrip } from "./MissionsStrip";
import { LocationToggle } from "./LocationToggle";

const BUILD_VERB = /\b(fix|add|change|update|build|remove|make)\b/i;
const BUILD_APP = /\b(halal|carcasstrack|nexus|avstones?|stone|oakton|meat|tv|kalshi)\b/i;
const LAST_PROMPT = /\bwhat('?s| was| is) my last prompt\b/i;
const PONY_PREFIX = /^\s*(\/ponytail\s+|ponytail\s*:\s*)/i;
const PONY_ASK = /\b(draft (me )?a prompt|ask ponytail)\b/i;
const isBuildAsk = (q) => PONY_PREFIX.test(q) || PONY_ASK.test(q) || (BUILD_VERB.test(q) && BUILD_APP.test(q));

const copyText = async (t) => {
  try { await navigator.clipboard.writeText(t); }
  catch (e) {
    const el = document.createElement("textarea"); el.value = t; document.body.appendChild(el); el.select();
    try { document.execCommand("copy"); } catch (e2) { el.remove(); return toast.error("Copy blocked — select the text instead"); }
    el.remove();
  }
  toast.success("Prompt copied");
};

const PonytailCard = ({ m }) => (
  <div className="w-full max-w-[85%] rounded-2xl rounded-bl-sm px-4 py-3 text-sm bg-[#140a1a] border border-pink-400/40 border-l-[3px] border-l-pink-400" data-testid="chat-ponytail-card">
    <div className="flex items-start justify-between gap-3">
      <div>
        <p className="nx-mono text-[10px] tracking-[0.3em] text-pink-400 font-bold" data-testid="chat-ponytail-label">PONYTAIL</p>
        <p className="nx-orbitron font-bold tracking-wider text-pink-300" data-testid="chat-ponytail-title">{m.title}</p>
      </div>
      <button onClick={() => copyText(m.prompt)} data-testid="chat-ponytail-copy"
        className="shrink-0 flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] nx-mono text-pink-100 border border-pink-300/40 hover:bg-pink-400/10"><Copy className="w-3 h-3" /> Copy</button>
    </div>
    <pre className="mt-2 whitespace-pre-wrap text-pink-50/90 text-[13px] leading-relaxed font-sans max-h-80 overflow-y-auto" data-testid="chat-ponytail-prompt">{m.prompt}</pre>
    <p className="mt-2 nx-mono text-[10px] tracking-widest text-pink-300/60" data-testid="chat-ponytail-tag">{m.note || "Optimized by Ponytail"}</p>
  </div>
);

const dayKey = (iso) => (iso ? new Date(iso).toDateString() : "");
const DaySep = ({ iso }) => {
  const d = new Date(iso), t = new Date(), y = new Date(Date.now() - 864e5);
  const label = d.toDateString() === t.toDateString() ? "Today" : d.toDateString() === y.toDateString() ? "Yesterday"
    : d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: d.getFullYear() === t.getFullYear() ? undefined : "numeric" });
  return <div className="flex justify-center" data-testid="nexus-chat-day-sep"><span className="px-3 py-0.5 rounded-full text-[10px] nx-mono tracking-widest text-cyan-200/50 bg-white/5">{label}</span></div>;
};
const MOOD = { danger: "#EF4444", good: "#10B981", neutral: "#00F0FF" };
const _previewCache = new WeakMap();
const imgPreview = (f) => {
  if (!f || !f.type || !f.type.startsWith("image/")) return null;
  if (!_previewCache.has(f)) _previewCache.set(f, URL.createObjectURL(f));
  return _previewCache.get(f);
};
const SUGGESTIONS = [
  "How's my business today?",
  "Summarize my day",
  "Any risks I should know about?",
  "Draft a polite reply for me",
  "What should I focus on next?",
];

export default function NexusChat({ session }) {
  const userId = session.user_id;
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [files, setFiles] = useState([]);
  const [phase, setPhase] = useState("idle"); // idle|transcribing|thinking|speaking
  const [recording, setRecording] = useState(false);
  const [thinkHard, setThinkHard] = useState(false);
  const [autoSpeak, setAutoSpeak] = useState(() => {
    try { return localStorage.getItem("nexus_autospeak") === "1"; } catch (e) { return false; }
  });
  const [loading, setLoading] = useState(true);
  const [batchMode, setBatchMode] = useState(() => {
    try { return localStorage.getItem("nexus_batch_mode") !== "0"; } catch (e) { return true; }
  });
  const [queue, setQueue] = useState([]);
  useEffect(() => { document.body.classList.add("nx-chat-open"); return () => document.body.classList.remove("nx-chat-open"); }, []);
  const inputRef = useRef(null);
  const [view, setView] = useState("chat");
  const [unread, setUnread] = useState(0);
  useEffect(() => { channelVideos({ limit: 100, full: false }).then((d) => setUnread(d.items.filter((v) => !v.read).length)).catch(() => {}); }, []);

  const audioRef = useRef(null);
  const scrollRef = useRef(null);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const busyRef = useRef(false);
  const fileRef = useRef(null);

  const [older, setOlder] = useState({ more: false, oldest: null, busy: false });
  const [pending, setPending] = useState(false);
  const keepPosRef = useRef(null);

  const applyHistory = useCallback((hist) => {
    setMessages(hist.messages || []);
    setOlder({ more: !!hist.has_more, oldest: hist.oldest, busy: false });
    setPending(!!hist.pending);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const [ch, hist] = await Promise.all([nexusChannel(userId), nexusChatHistory(userId)]);
        setSessionId(ch.session_id);
        applyHistory(hist);
      } catch (e) { toast.error("Could not open Nexus chat"); }
      finally { setLoading(false); }
    })();
  }, [userId, applyHistory]);

  // a reply that was still processing when you left: poll every 3s until it lands
  useEffect(() => {
    if (!pending || busyRef.current) return undefined;
    const t = setInterval(async () => {
      try {
        const hist = await nexusChatHistory(userId);
        if (!hist.pending) applyHistory(hist);
      } catch (e) { /* keep polling */ }
    }, 3000);
    return () => clearInterval(t);
  }, [pending, userId, applyHistory]);

  const loadOlder = useCallback(async () => {
    if (!older.more || older.busy || !older.oldest) return;
    setOlder((o) => ({ ...o, busy: true }));
    try {
      const hist = await nexusChatHistory(userId, older.oldest);
      const el = scrollRef.current;
      keepPosRef.current = el ? el.scrollHeight - el.scrollTop : null;
      setMessages((prev) => [...(hist.messages || []), ...prev]);
      setOlder({ more: !!hist.has_more, oldest: hist.oldest, busy: false });
    } catch (e) { setOlder((o) => ({ ...o, busy: false })); }
  }, [older, userId]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (keepPosRef.current != null) { el.scrollTop = el.scrollHeight - keepPosRef.current; keepPosRef.current = null; return; }
    el.scrollTop = el.scrollHeight;
  }, [messages, phase, pending]);

  const toggleAutoSpeak = () => {
    setAutoSpeak((v) => { const n = !v; try { localStorage.setItem("nexus_autospeak", n ? "1" : "0"); } catch (e) {} return n; });
  };

  const playKey = useCallback((key) => {
    if (!key || !audioRef.current) return;
    audioRef.current.src = audioUrlForKey(key);
    audioRef.current.play().catch(() => {});
  }, []);

  const pushMsg = (m) => setMessages((prev) => [...prev, { id: Math.random().toString(36).slice(2), ...m }]);

  // Poll the channel until the background "Think Hard" council answer lands (beats the 60s gateway limit).
  const waitForCouncil = useCallback(async (prevTurnId) => {
    for (let i = 0; i < 120; i++) {
      await new Promise((res) => setTimeout(res, i < 10 ? 1500 : 3000));
      try {
        const ch = await nexusChannel(userId);
        if ((ch.turn_id || 0) > (prevTurnId || 0) && ch.reply_text) {
          return { reply_text: ch.reply_text, audio_key: ch.audio_key, mood: ch.mood || "neutral" };
        }
        if (ch.error && ch.status !== "thinking") return { error: ch.error };
      } catch (e) { /* keep polling */ }
    }
    return { error: "timeout" };
  }, [userId]);
  const failReason = (e) => e?.response?.data?.detail || (e?.code === "ECONNABORTED" ? "timeout" : e?.response?.status ? `HTTP ${e.response.status}` : e?.message || "network error");

  const genRef = useRef(0);
  const lastAskRef = useRef(null);
  const stop = useCallback(() => {
    genRef.current += 1;
    try { audioRef.current?.pause(); } catch (e) {}
    nexusCancel(userId).catch(() => {});
    const last = lastAskRef.current;
    if (busyRef.current && last) {
      setMessages((prev) => prev.filter((m) => m.id !== last.id && !(last.ids || []).includes(m.id)));
      setText(last.text); setFiles(last.files || []);
    }
    busyRef.current = false; setPhase("idle");
  }, [userId]);

  const sendText = useCallback(async (raw, bubbles) => {
    const q = (raw ?? text).trim();
    const sentFiles = files;
    const hasFiles = sentFiles.length > 0;
    if (/^(stop|cancel)[.!]?$/i.test(q)) { setText(""); stop(); return; }
    if ((!q && !hasFiles) || busyRef.current) return;
    busyRef.current = true;
    const gen = ++genRef.current;
    setText("");
    setFiles([]);
    const marker = hasFiles ? `\n\n[attached: ${sentFiles.map((f) => f.name).join(", ")}]` : "";
    const uid = Math.random().toString(36).slice(2);
    const parts = bubbles && bubbles.length > 1 ? bubbles.map((t, i) => ({ id: `${uid}-${i}`, role: "user", text: t + (i === bubbles.length - 1 ? marker : "") })) : [{ id: uid, role: "user", text: (q || "Shared this with you") + marker }];
    lastAskRef.current = { id: uid, ids: parts.map((m) => m.id), text: q, files: sentFiles };
    setMessages((prev) => [...prev, ...parts]);
    setPhase(thinkHard ? "council" : "thinking");
    if (!hasFiles && (/^floor:/i.test(q) || /^(halt|resume) floor\b/i.test(q))) {
      try {
        let r;
        if (/^halt floor/i.test(q)) r = await floorHalt();
        else if (/^resume floor/i.test(q)) r = await floorResume();
        else {
          r = await floorCommand(q.replace(/^floor:\s*/i, ""));
          while (r.status === "working" && gen === genRef.current) {
            await new Promise((ok) => setTimeout(ok, 5000));
            r = await floorJob(r.job_id);
          }
          if (r.status === "error") throw new Error(r.error);
        }
        const acts = (r.actions || []).map((a) => `• ${a.type} ${a.ticker || a.position_id || ""}: ${a.outcome}`).join("\n");
        if (gen === genRef.current) pushMsg({ role: "nexus", text: `[Floor] ${r.report}${acts ? `\n${acts}` : ""}` });
      } catch (e) { if (gen === genRef.current) toast.error(e?.response?.data?.detail || e?.message || "Floor Chief failed"); }
      finally { if (gen === genRef.current) { busyRef.current = false; setPhase("idle"); lastAskRef.current = null; } }
      return;
    }
    if (!hasFiles && (LAST_PROMPT.test(q) || isBuildAsk(q))) {
      try {
        let p;
        if (LAST_PROMPT.test(q)) {
          const d = await ponytailLast(userId);
          if (!d.item) { pushMsg({ role: "nexus", text: "You don't have a Ponytail prompt yet, sir." }); return; }
          p = { ...d.item, note: `Your last Ponytail prompt · ${new Date(d.item.created_at).toLocaleString()}` };
        } else {
          p = await ponytailCraft(userId, [q.replace(PONY_PREFIX, "").trim() || q], "auto");
          while (p.job_id && gen === genRef.current) {
            await new Promise((ok) => setTimeout(ok, 4000));
            const j = await ponytailJob(userId, p.job_id);
            if (j.status === "error") throw new Error(j.error);
            if (j.status === "done") p = j.result;
          }
        }
        if (gen === genRef.current) pushMsg({ role: "nexus", kind: "ponytail", title: p.title, prompt: p.prompt, note: p.note });
      } catch (e) { if (gen === genRef.current) toast.error(e?.response?.data?.detail || "Ponytail could not write it"); }
      finally { if (gen === genRef.current) { busyRef.current = false; setPhase("idle"); lastAskRef.current = null; } }
      return;
    }
    try {
      const r = hasFiles
        ? await nexusCommandAttach(userId, q, sentFiles, "chat", thinkHard)
        : await nexusCommand(userId, q, "chat", thinkHard);
      if (gen !== genRef.current || r.cancelled) return;
      if (r.pending) {
        const done = await waitForCouncil(r.prev_turn_id);
        if (gen !== genRef.current) return;
        if (done.error) {
          toast.error(`Nexus could not answer: ${done.error}`);
        } else {
          pushMsg({ role: "nexus", text: done.reply_text, audio_key: done.audio_key, mood: done.mood });
          if (autoSpeak && done.audio_key) { setPhase("speaking"); playKey(done.audio_key); }
        }
      } else {
        pushMsg({ role: "nexus", text: r.reply_text || "", audio_key: r.audio_key, mood: r.mood || "neutral" });
        if (autoSpeak && r.audio_key) { setPhase("speaking"); playKey(r.audio_key); }
      }
    } catch (e) { if (gen === genRef.current) toast.error(`Nexus could not answer: ${failReason(e)}`); }
    finally { if (gen === genRef.current) { busyRef.current = false; setPhase((p) => (p === "speaking" ? p : "idle")); lastAskRef.current = null; } }
  }, [text, files, userId, autoSpeak, playKey, thinkHard, waitForCouncil, stop]);

  const toggleBatch = () => {
    setBatchMode((v) => { const n = !v; try { localStorage.setItem("nexus_batch_mode", n ? "1" : "0"); } catch (e) {} return n; });
  };
  const queueText = () => {
    const q = text.trim();
    if (!q) return;
    if (/^(stop|cancel)[.!]?$/i.test(q)) { setText(""); stop(); return; }
    if (queue.length >= 10) return;
    setQueue((p) => [...p, q]); setText(""); inputRef.current?.focus();
  };
  const startBatch = () => {
    const extra = text.trim();
    const all = extra && queue.length < 10 ? [...queue, extra] : queue;
    if (!all.length && !files.length) return;
    if (busyRef.current) return;
    const joined = all.length > 1 ? all.map((t, i) => `${i + 1}) ${t}`).join("\n") : (all[0] || "");
    setQueue([]);
    sendText(joined, all);
  };
  const submit = () => (batchMode ? queueText() : sendText());

  const pickFiles = (e) => {
    const chosen = Array.from(e.target.files || []);
    if (chosen.length) setFiles((p) => [...p, ...chosen]);
    e.target.value = "";
  };

  const handlePaste = (e) => {
    const items = Array.from(e.clipboardData?.items || []);
    const imgs = [];
    items.forEach((it) => {
      if (it.type && it.type.startsWith("image/")) {
        const f = it.getAsFile();
        if (f) {
          const ext = (it.type.split("/")[1] || "png").split("+")[0];
          imgs.push(new File([f], f.name && /\.\w+$/.test(f.name) ? f.name : `pasted-${Date.now()}.${ext}`, { type: it.type }));
        }
      }
    });
    if (imgs.length) {
      e.preventDefault();
      setFiles((p) => [...p, ...imgs]);
      toast.success(imgs.length === 1 ? "Image pasted — hit send" : `${imgs.length} images pasted`);
    }
  };

  const sendBlob = useCallback(async (blob) => {
    if (!sessionId || !blob || blob.size < 400) { setPhase("idle"); if (blob && blob.size < 400) toast.message("Didn't catch that — tap and speak a little longer."); return; }
    busyRef.current = true;
    setPhase("transcribing");
    try {
      const r = await nexusTalk(sessionId, userId, blob);
      if (r.user_text) pushMsg({ role: "user", text: r.user_text });
      setPhase("thinking");
      pushMsg({ role: "nexus", text: r.reply_text || "", audio_key: r.audio_key, mood: r.mood || "neutral" });
      if (autoSpeak && r.audio_key) { setPhase("speaking"); playKey(r.audio_key); }
    } catch (e) { toast.error(`Nexus could not answer: ${failReason(e)}`); }
    finally { busyRef.current = false; setPhase("idle"); }
  }, [sessionId, userId, autoSpeak, playKey]);

  const startRecording = useCallback(async () => {
    if (recording || busyRef.current) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      let mime = "";
      for (const c of ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg"]) {
        if (window.MediaRecorder && MediaRecorder.isTypeSupported(c)) { mime = c; break; }
      }
      const mr = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
        stream.getTracks().forEach((t) => t.stop());
        setRecording(false);
        sendBlob(blob);
      };
      mediaRef.current = mr; mr.start(); setRecording(true);
    } catch (e) { toast.error("Microphone blocked — allow mic access, or just type."); setRecording(false); }
  }, [recording, sendBlob]);

  const stopRecording = useCallback(() => {
    if (mediaRef.current?.state === "recording") { try { mediaRef.current.stop(); } catch (e) {} }
  }, []);

  const clearChat = async () => {
    if (!window.confirm("Delete all messages? This cannot be undone.")) return;
    try { await nexusChatClear(userId); setMessages([]); setOlder({ more: false, oldest: null, busy: false }); setPending(false); toast.success("Chat cleared"); }
    catch (e) { toast.error("Could not clear"); }
  };

  const statusLabel = phase === "transcribing" ? "Hearing you…" : phase === "council" ? "Convening the council…" : phase === "thinking" ? "Nexus is thinking…"
    : phase === "speaking" ? "Nexus is speaking…" : recording ? "Recording — tap to send" : "";

  return (
    <div className="h-full bg-[#030712] nx-display flex flex-col relative" data-testid="nexus-chat">
      <audio ref={audioRef} onEnded={() => setPhase("idle")} />
      <HerculesScorecard />
      <FloorLockStrip />

      {/* header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#00F0FF22] shrink-0">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: "radial-gradient(circle at 50% 35%, #00F0FF, #061018 72%)", boxShadow: "0 0 18px #00F0FF88" }}>
            <Sparkles className="w-4 h-4 text-white" />
          </div>
          <div>
            <p className="nx-orbitron font-black tracking-[0.25em] text-[#00F0FF] text-sm leading-none">NEXUS</p>
            <p className="text-cyan-100/40 text-[10px] nx-mono flex items-center gap-2">talk or type · he remembers
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border border-pink-400/40 text-pink-300 tracking-widest" data-testid="nexus-chat-ponytail-badge"><Link2 className="w-2.5 h-2.5" /> PONYTAIL LINKED</span></p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <LiveClock className="hidden sm:block mr-1" />
          <button onClick={() => setView((v) => (v === "anne" ? "chat" : "anne"))} data-testid="nexus-tab-anne"
            className={`shrink-0 px-4 py-2 rounded-full nx-mono text-[11px] tracking-widest font-black flex items-center gap-1.5 border-2 transition-colors ${view === "anne" ? "bg-[#030712] text-[#FF3D68] border-[#FF3D68]" : "bg-[#FF3D68] text-white border-[#FF3D68] shadow-[0_0_14px_#FF3D6888]"}`}>
            <ShieldCheck className="w-3.5 h-3.5" /> {view === "anne" ? "BACK TO NEXUS" : "MISS ANNE"}
          </button>
          <LocationToggle />
          <button onClick={() => setView((v) => (v === "videos" ? "chat" : "videos"))} data-testid="nexus-tab-videos"
            className={`relative px-3 py-1.5 rounded-full nx-mono text-[10px] tracking-widest font-bold border ${view === "videos" ? "bg-[#00F0FF] text-[#030712] border-[#00F0FF]" : "text-cyan-200/70 border-cyan-200/20"}`}>
            {view === "videos" ? "CHAT" : "VIDEOS"}
            {view !== "videos" && unread > 0 && <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-[#00F0FF]" data-testid="nexus-tab-videos-dot" />}
          </button>
          <button onClick={() => setThinkHard((v) => !v)} data-testid="nexus-chat-thinkhard"
            className={`px-3 py-1.5 rounded-full nx-mono text-[10px] tracking-widest font-bold flex items-center gap-1.5 border transition-colors ${thinkHard ? "text-[#030712] bg-[#00F0FF] border-[#00F0FF]" : "text-cyan-200/60 border-cyan-200/25"}`}
            title="Think Hard — Nexus convenes his full council of brains for big questions">
            <Brain className="w-3.5 h-3.5" /> {thinkHard ? "THINK HARD" : "THINK HARD"}
          </button>
          <button onClick={toggleAutoSpeak} data-testid="nexus-chat-autospeak"
            className={`px-3 py-1.5 rounded-full nx-mono text-[10px] tracking-widest font-bold flex items-center gap-1.5 border transition-colors ${autoSpeak ? "text-[#030712] bg-[#00F0FF] border-[#00F0FF]" : "text-cyan-200/60 border-cyan-200/25"}`}>
            {autoSpeak ? <Volume2 className="w-3.5 h-3.5" /> : <VolumeX className="w-3.5 h-3.5" />} {autoSpeak ? "SPEAKS" : "SILENT"}
          </button>
          <button onClick={clearChat} data-testid="nexus-chat-clear" className="px-3 py-1.5 rounded-full nx-mono text-[10px] tracking-widest font-bold flex items-center gap-1.5 text-cyan-200/60 hover:text-red-400 border border-cyan-200/20 transition-colors" title="Clear all messages">
            <Trash2 className="w-3.5 h-3.5" /> CLEAR ALL
          </button>
        </div>
      </div>

      {view === "videos" && <div className="absolute inset-x-0 bottom-0 top-[57px] z-30 bg-[#030712]"><ChannelVideosTab onUnread={setUnread} /></div>}
      {view === "anne" && <div className="absolute inset-x-0 bottom-0 top-[57px] z-30 bg-[#030712]"><NexusAnneTab onClose={() => setView("chat")} /></div>}

      {/* thread */}
      <div ref={scrollRef} onScroll={(e) => { if (e.currentTarget.scrollTop < 40) loadOlder(); }} className="flex-1 overflow-y-auto px-4 py-5 space-y-3" data-testid="nexus-chat-thread">
        {older.more && (
          <div className="flex justify-center">
            <button onClick={loadOlder} data-testid="nexus-chat-load-older" className="px-3 py-1 rounded-full text-[10px] nx-mono tracking-widest text-cyan-200/60 border border-cyan-200/20 flex items-center gap-1">
              {older.busy && <Loader2 className="w-3 h-3 animate-spin" />} LOAD OLDER
            </button>
          </div>
        )}
        {loading && <div className="flex justify-center pt-10"><Loader2 className="w-6 h-6 text-cyan-400 animate-spin" /></div>}
        {!loading && messages.length === 0 && (
          <div className="text-center pt-10 space-y-2">
            <p className="text-cyan-100/70 text-base">Good to see you, sir. What's on your mind?</p>
            <p className="text-cyan-100/35 text-xs nx-mono">Type below, or tap the mic to talk.</p>
          </div>
        )}
        {messages.map((m, i) => {
          const isUser = m.role === "user";
          const c = MOOD[m.mood] || "#00F0FF";
          const sep = m.created_at && dayKey(m.created_at) !== dayKey(messages[i - 1]?.created_at) ? <DaySep iso={m.created_at} /> : null;
          if (m.kind === "ponytail") return <React.Fragment key={m.id}>{sep}<div className="flex justify-start"><PonytailCard m={m} /></div></React.Fragment>;
          return (
            <React.Fragment key={m.id}>{sep}
            <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
              <div data-testid={`nexus-msg-${m.role}`}
                className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${isUser ? "bg-[#00F0FF] text-[#030712] rounded-br-sm font-medium" : "text-cyan-50 rounded-bl-sm"}`}
                style={isUser ? {} : { background: "#0a1526", border: `1px solid ${c}44`, borderLeft: `3px solid ${c}` }}>
                {isUser ? m.text : <Linkified text={m.text} />}
                {!isUser && m.audio_key && (
                  <button onClick={() => playKey(m.audio_key)} data-testid="nexus-msg-play"
                    className="ml-2 inline-flex items-center align-middle text-[10px] nx-mono opacity-70 hover:opacity-100" style={{ color: c }}>
                    <Play className="w-3 h-3 mr-0.5" /> hear
                  </button>
                )}
                {isUser && m.status === "error" && <span className="block text-[10px] nx-mono text-rose-700 mt-1" data-testid="nexus-msg-error">not answered: {m.error}</span>}
              </div>
            </div>
            </React.Fragment>
          );
        })}
        {phase === "idle" && pending && (
          <div className="flex justify-start"><div className="rounded-2xl px-4 py-2.5 text-xs nx-mono text-cyan-300/70 bg-[#0a1526] border border-[#00F0FF33] flex items-center gap-2" data-testid="nexus-chat-pending"><Loader2 className="w-3.5 h-3.5 animate-spin" /> thinking...</div></div>
        )}
        {phase !== "idle" && (
          <div className="flex justify-start"><div className="rounded-2xl px-4 py-2.5 text-xs nx-mono text-cyan-300/70 bg-[#0a1526] border border-[#00F0FF33] flex items-center gap-2" data-testid="nexus-chat-status"><Loader2 className="w-3.5 h-3.5 animate-spin" /> {statusLabel}</div></div>
        )}
      </div>

      {/* suggestions */}
      {messages.length < 2 && !loading && (
        <div className="px-4 pb-2 flex flex-wrap gap-2" data-testid="nexus-chat-suggestions">
          {SUGGESTIONS.map((s) => (
            <button key={s} onClick={() => sendText(s)} data-testid="nexus-suggestion"
              className="px-3 py-1.5 rounded-full text-xs nx-mono text-cyan-100/80 border border-[#00F0FF33] hover:bg-[#00F0FF11] transition-colors">
              {s}
            </button>
          ))}
        </div>
      )}

      {/* attached files */}
      {files.length > 0 && (
        <div className="px-4 pb-1 flex flex-wrap gap-2" data-testid="nexus-chat-file-chips">
          {files.map((f, i) => {
            const preview = imgPreview(f);
            return (
              <span key={i} className="text-xs text-cyan-100 bg-[#00F0FF11] border border-[#00F0FF33] rounded-full pl-1.5 pr-3 py-1 flex items-center gap-2">
                {preview ? (
                  <img src={preview} alt={f.name} data-testid={`nexus-chat-file-thumb-${i}`} className="w-8 h-8 rounded-full object-cover border border-[#00F0FF44]" />
                ) : (
                  <Paperclip className="w-3 h-3 ml-1" />
                )}
                <span className="max-w-[130px] truncate">{f.name}</span>
                <button onClick={() => setFiles((p) => p.filter((_, j) => j !== i))} data-testid={`nexus-chat-file-remove-${i}`} className="text-cyan-200/50 hover:text-red-400"><X className="w-3 h-3" /></button>
              </span>
            );
          })}
        </div>
      )}

      <MissionsStrip />

      {/* batch queue */}
      {batchMode && queue.length > 0 && (
        <div className="px-4 pb-1 flex flex-wrap items-center gap-2" data-testid="nexus-chat-queue">
          {queue.map((q, i) => (
            <span key={i} data-testid={`nexus-chat-queue-item-${i}`} className="text-xs text-cyan-100 bg-[#00F0FF11] border border-[#00F0FF33] rounded-full pl-1 pr-2 py-1 flex items-center gap-2 max-w-full">
              <span className="w-5 h-5 rounded-full bg-[#00F0FF] text-[#030712] font-bold flex items-center justify-center shrink-0 text-[10px]">{i + 1}</span>
              <span className="max-w-[220px] truncate">{q}</span>
              <button onClick={() => setQueue((p) => p.filter((_, j) => j !== i))} data-testid={`nexus-chat-queue-remove-${i}`} className="text-cyan-200/50 hover:text-red-400"><X className="w-3 h-3" /></button>
            </span>
          ))}
          <button onClick={() => setQueue([])} data-testid="nexus-chat-queue-clear" className="text-[11px] nx-mono text-cyan-200/60 hover:text-red-400 underline">Clear</button>
          {queue.length >= 10 && <span className="text-[11px] nx-mono text-amber-300" data-testid="nexus-chat-queue-max">Max 10, press Start</span>}
        </div>
      )}

      {/* composer */}
      <div className="px-3 md:px-4 py-2.5 md:py-3 border-t border-[#00F0FF22] shrink-0 flex flex-wrap md:flex-nowrap items-end gap-2" data-testid="nexus-chat-composer">
        <input ref={fileRef} type="file" multiple className="hidden" onChange={pickFiles} data-testid="nexus-chat-file-input" />
        <textarea ref={inputRef} rows={1} value={text} onPaste={handlePaste}
          onChange={(e) => { setText(e.target.value); const el = e.target; el.style.height = "auto"; el.style.height = `${Math.min(el.scrollHeight, 132)}px`; }}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(); if (inputRef.current) inputRef.current.style.height = "auto"; } }}
          placeholder={files.length ? "Add a note, or just send…" : batchMode ? "Message Nexus… (queues until Start)" : "Message Nexus…"} data-testid="nexus-chat-input"
          className="order-1 md:order-2 basis-full md:basis-auto md:flex-1 min-w-0 resize-none bg-[#0a1526] text-cyan-50 border border-[#00F0FF33] rounded-2xl px-4 py-3 text-base md:text-sm leading-snug focus:outline-none focus:border-[#00F0FF] max-h-[132px] overflow-y-auto" />
        <div className="order-2 md:order-1 flex items-center gap-2">
          <button onClick={() => fileRef.current?.click()} data-testid="nexus-chat-attach"
            className="w-10 h-10 md:w-11 md:h-11 rounded-full flex items-center justify-center shrink-0 border text-[#00F0FF] border-[#00F0FF55] hover:bg-[#00F0FF11] transition-colors"
            title="Attach files to feed Nexus">
            <Paperclip className="w-5 h-5" />
          </button>
          <button onClick={() => (recording ? stopRecording() : startRecording())} data-testid="nexus-chat-mic"
            className={`w-10 h-10 md:w-11 md:h-11 rounded-full flex items-center justify-center shrink-0 border transition-colors ${recording ? "bg-[#EF4444] border-[#EF4444] text-white animate-pulse" : "text-[#00F0FF] border-[#00F0FF55] hover:bg-[#00F0FF11]"}`}
            title={recording ? "Tap to send" : "Tap to talk"}>
            {phase === "transcribing" || phase === "thinking" || phase === "council" ? <Loader2 className="w-5 h-5 animate-spin" /> : recording ? <Square className="w-4 h-4" /> : <Mic className="w-5 h-5" />}
          </button>
          <button onClick={toggleBatch} data-testid="nexus-chat-batch-toggle" aria-pressed={batchMode}
            className={`relative w-10 h-10 md:w-11 md:h-11 rounded-full flex items-center justify-center shrink-0 border transition-colors ${batchMode ? "bg-[#00F0FF22] text-[#00F0FF] border-[#00F0FF]" : "text-cyan-200/40 border-cyan-200/20 hover:text-cyan-100"}`}
            title={batchMode ? "Batch ON: Enter queues, Start sends all" : "Batch OFF: Enter sends immediately"}>
            <Layers className="w-5 h-5" />
            <span className={`absolute -bottom-1 left-1/2 -translate-x-1/2 px-1 rounded text-[8px] font-bold nx-mono leading-[12px] ${batchMode ? "bg-[#00F0FF] text-[#030712]" : "bg-[#1e293b] text-cyan-200/60"}`}>{batchMode ? "ON" : "OFF"}</span>
          </button>
        </div>
        <div className="order-2 flex-1 md:hidden" />
        <div className="order-3 flex items-center gap-2">
          {phase !== "idle" ? (
            <button onClick={stop} data-testid="nexus-chat-stop" title="Stop: throw away this answer and edit your message"
              className="w-10 h-10 md:w-auto md:h-11 md:px-5 rounded-full flex items-center justify-center gap-2 shrink-0 bg-[#EF4444] text-white font-bold nx-orbitron tracking-widest">
              <Square className="w-4 h-4 fill-white" /><span className="hidden md:inline">STOP</span>
            </button>
          ) : (
            <>
              <button onClick={submit} disabled={batchMode ? (!text.trim() || queue.length >= 10) : (!text.trim() && files.length === 0)} data-testid="nexus-chat-send"
                title={batchMode ? "Add to queue" : "Send"}
                className="w-10 h-10 md:w-11 md:h-11 rounded-full flex items-center justify-center shrink-0 bg-[#00F0FF] text-[#030712] disabled:opacity-30">
                <Send className="w-5 h-5" />
              </button>
              {batchMode && (
                <button onClick={startBatch} disabled={queue.length === 0 && !text.trim()} data-testid="nexus-chat-start" title="Send all queued messages"
                  className="h-10 md:h-11 px-3 md:px-4 rounded-full flex items-center gap-1.5 shrink-0 bg-[#10B981] text-[#030712] font-bold nx-orbitron text-xs tracking-widest disabled:opacity-30">
                  <PlayCircle className="w-4 h-4" /> START{queue.length ? ` ${queue.length}` : ""}
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
